import {
  addBracketToGroup,
  createEntry,
  fetchBracketWithDetails,
  fetchEntries,
  fetchEntryById,
  fetchGroups,
  fetchGroupsForBracket,
  fetchLeaderboardRankings,
  fetchOfficialEntry,
  fetchTournamentBracketsWithEntries,
  fetchUserFirstName,
  removeBracketFromGroup,
  saveEntry,
  savePoolTiebreaks,
  updateBracketOfficialEntryId,
  updateBracketStatus,
} from '@/services/bracketService'
import { computeDownstreamClears } from '@/lib/pickCascade'
import {
  computePoolStandings,
  deriveSundayMatchups,
} from '@/lib/poolStandings'
import { create } from 'zustand'

// ─── Derived-state helpers (pure functions, no store access) ───

function computeBracketStructure(matches, picks) {
  if (!matches.length) return []

  const roundsMap = {}
  const seedsByRound = {}

  matches.forEach(match => {
    if (!seedsByRound[match.round]) seedsByRound[match.round] = []
    seedsByRound[match.round].push(match)
  })

  const roundNumbers = Object.keys(seedsByRound).map(Number).sort((a, b) => a - b)
  const hasPQ = roundNumbers.includes(0)
  const baseRound = hasPQ ? 1 : Math.min(...roundNumbers.filter(r => r > 0))
  const baseRoundMatches = seedsByRound[baseRound] || []

  if (baseRoundMatches.length === 0) return []

  const totalRounds = Math.ceil(Math.log2(baseRoundMatches.length)) + 1

  for (let round = (hasPQ ? 0 : baseRound); round < baseRound + totalRounds; round++) {
    if (!roundsMap[round]) roundsMap[round] = []

    if (seedsByRound[round]) {
      roundsMap[round] = seedsByRound[round]
    } else {
      let matchesInRound
      if (round === 0) {
        matchesInRound = seedsByRound[0]?.length || 0
      } else {
        const roundsSinceBase = round - baseRound
        matchesInRound = Math.max(1, Math.floor(baseRoundMatches.length / Math.pow(2, roundsSinceBase)))
      }

      for (let matchNum = 1; matchNum <= matchesInRound; matchNum++) {
        let feedsIntoRound = null
        let feedsIntoMatchNumber = null
        let feedsIntoSlot = null

        if (round < baseRound + totalRounds - 1) {
          feedsIntoRound = round + 1
          feedsIntoMatchNumber = Math.ceil(matchNum / 2)
          feedsIntoSlot = matchNum % 2 === 1 ? 1 : 2
        }

        roundsMap[round].push({
          match_id: `placeholder-r${round}-m${matchNum}`,
          round,
          match_number: matchNum,
          team_one_id: null,
          team_two_id: null,
          feeds_into_round: feedsIntoRound,
          feeds_into_match_number: feedsIntoMatchNumber,
          feeds_into_slot: feedsIntoSlot,
        })
      }
    }
  }

  Object.entries(picks).forEach(([matchId, pick]) => {
    if (pick.round !== undefined && pick.match_number !== undefined) {
      if (roundsMap[pick.round]) {
        const existingMatch = roundsMap[pick.round].find(m => m.match_number === pick.match_number)
        if (existingMatch) {
          const seedMatch = matches.find(m => m.round === pick.round && m.match_number === pick.match_number)
          Object.assign(existingMatch, {
            match_id: matchId,
            team_one_id: seedMatch?.team_one_id || existingMatch.team_one_id,
            team_two_id: seedMatch?.team_two_id || existingMatch.team_two_id,
            feeds_into_round: seedMatch?.feeds_into_round || existingMatch.feeds_into_round,
            feeds_into_match_number: seedMatch?.feeds_into_match_number || existingMatch.feeds_into_match_number,
            feeds_into_slot: seedMatch?.feeds_into_slot || existingMatch.feeds_into_slot,
          })
        }
      }
    }
  })

  Object.keys(roundsMap).forEach(round => {
    roundsMap[round].sort((a, b) => a.match_number - b.match_number)
  })

  const bracketRounds = []
  const sortedRounds = Object.keys(roundsMap).map(Number).sort((a, b) => a - b)
  sortedRounds.forEach(roundNum => {
    bracketRounds.push({ roundNum, matches: roundsMap[roundNum] })
  })

  return bracketRounds
}

function computeBracketRoundCount(matches) {
  if (!matches.length) return 0
  const roundsMap = {}
  matches.forEach(match => {
    if (!roundsMap[match.round]) roundsMap[match.round] = []
    roundsMap[match.round].push(match)
  })
  const hasPQ = !!roundsMap[0]
  const round1Matches = roundsMap[1] || []
  const totalRounds = round1Matches.length > 0
    ? Math.ceil(Math.log2(round1Matches.length)) + 1
    : 1
  return (hasPQ ? 1 : 0) + totalRounds
}

function computeHasPicksChanged(picks, originalPicks) {
  const currentKeys = Object.keys(picks)
  const originalKeys = Object.keys(originalPicks)
  if (currentKeys.length !== originalKeys.length) return true
  for (const key of currentKeys) {
    if (!originalPicks[key]) return true
    if (picks[key]?.teamId !== originalPicks[key]?.teamId) return true
  }
  return false
}

function computeUserSaveLabel(hasPicksChanged, isNewEntryMode, hasAnyPicks) {
  if (hasPicksChanged) return 'Save picks'
  if (isNewEntryMode && !hasAnyPicks) return 'Create picks'
  return 'Edit picks'
}

function getPointsForRound(roundNum) {
  if (roundNum === 0 || roundNum === 1) return 1
  return Math.pow(2, roundNum - 1)
}

// ─── Per-round lock state (multi-week brackets) ───
//
// A bracket opts into round-by-round locking by having rows in bracket_rounds
// (see 20260804000001_bracket_round_locks.sql). Without them every helper here
// returns null/false and the bracket keeps using the single bracket.status
// gate, exactly as before.
//
//   pending -> not announced; nobody writes
//   open    -> users pick this round
//   locked  -> users frozen; admin enters official results

function buildRoundStates(rounds) {
  if (!Array.isArray(rounds) || rounds.length === 0) return null
  const byRound = {}
  rounds.forEach(r => {
    byRound[r.round] = {
      round: r.round,
      label: r.label || null,
      status: r.status || 'pending',
      deadline: r.submission_deadline || null,
      matchupsPublished: !!r.matchups_published,
    }
  })
  return byRound
}

// Rounds with no row are 'pending': a round-locked bracket only lets through
// what the admin explicitly opened. Mirrors bracket_round_state() server-side.
function roundStatusOf(roundStates, roundNum) {
  if (!roundStates) return null
  return roundStates[roundNum]?.status || 'pending'
}

function isRoundEditable(roundStates, roundNum, isEditingOfficial) {
  const status = roundStatusOf(roundStates, roundNum)
  if (status === null) return true // legacy bracket -- caller's coarse gate decides
  return isEditingOfficial ? status === 'locked' : status === 'open'
}

// Human name for a round in a save toast: the admin's own label for it when
// there is one (multi-week brackets name their rounds), else the shape-derived
// name. Never throws on a round the structure doesn't know about.
function roundLabelFor(roundNum, roundStates, bracketStructure) {
  const custom = roundStates?.[roundNum]?.label
  if (custom) return custom
  const matchCount = bracketStructure?.find(r => r.roundNum === roundNum)?.matches?.length ?? 0
  return getRoundLabel(roundNum, matchCount)
}

function getRoundLabel(roundNum, matchCount) {
  if (roundNum === 0) return 'Pre-Qualifiers'
  if (matchCount === 1) return 'Championship'
  if (matchCount === 2) return 'Semifinals'
  if (matchCount === 4) return 'Quarterfinals'
  if (matchCount === 8) return 'Round of 16'
  if (matchCount === 16) return 'Round of 32'
  if (matchCount === 32) return 'Round of 64'
  if (matchCount === 64) return 'Round of 128'
  if (matchCount === 128) return 'Round of 256'
  return `Round ${roundNum}`
}

// Pool-play live-stats weights mirror pool_play_config defaults. Real scoring
// reads the live config row server-side; these are fast estimates for the UI.
const POOL_LIVE_WEIGHTS = {
  pool_winner: 1,
  sunday_winner: 4,
}

// An undecided Sunday pick is dead once both actual participants of that
// Sunday match are known (official advancer taps: position i of Pool A vs
// position i of Pool B) and the picked team isn't one of them. Mirrors the
// DUB-191 server rule in calculate_pool_entry_score; sunday_seed_index is
// 1-based, the tiebreak arrays are 0-based.
function isSundayPickStillWinnable(pickTeamId, sundaySeedIndex, officialTiebreaks) {
  if (!sundaySeedIndex || !officialTiebreaks) return true
  const actualA = officialTiebreaks.A?.[sundaySeedIndex - 1]
  const actualB = officialTiebreaks.B?.[sundaySeedIndex - 1]
  if (!actualA || !actualB) return true
  return pickTeamId === actualA || pickTeamId === actualB
}

function computeLiveStatsPool(picks, matches, officialPoolTiebreaks) {
  // Potential = points STILL winnable. Mirrors calculate_pool_entry_score:
  // count only picks that are not yet decided (no official result). A decided
  // pick — right or wrong — contributes 0, so potential decreases as you earn
  // and reaches 0 once every match is decided (DUB-167). A match with no pick
  // can never score, so removing a pick lowers potential too (DUB-142). An
  // undecided Sunday pick on a team that isn't in the actual matchup can never
  // score either (DUB-191).
  let potentialPoints = 0
  matches.forEach(m => {
    if (m.match_kind !== 'pool' && m.match_kind !== 'sunday') return
    const pick = Object.values(picks).find(p => p?.round === m.round && p?.match_number === m.match_number)
    if (!pick?.teamId) return // no pick made → not reachable
    const weight = m.match_kind === 'sunday' ? POOL_LIVE_WEIGHTS.sunday_winner : POOL_LIVE_WEIGHTS.pool_winner
    // undecided pick only → still reachable. Decided (correct or wrong) → 0.
    if (!pick.winner_team_id) {
      if (
        m.match_kind === 'sunday'
        && !isSundayPickStillWinnable(pick.teamId, m.sunday_seed_index, officialPoolTiebreaks)
      ) return
      potentialPoints += weight
    }
  })
  return {
    potentialPoints,
    hasChampionPick: false,
    championTeamId: null,
    championWon: false,
  }
}

// A round whose matchups the league AUTHORS (matchupsPublished) is played by
// the real field, so the seed row's two teams are the only ones a pick for that
// match can name. A pick naming anyone else is orphaned -- it survives from a
// page loaded before the matchups were published, and can never match the
// official winner. It must not count toward potential points (DUB-292: the
// first quarterfinal showed "+1" with no team highlighted, because the saved
// pick pointed at the play-in row for the same club rather than the
// quarterfinal row that replaced it).
function isPickOrphaned(pick, matches, roundStates) {
  if (!pick?.teamId) return false
  if (!roundStates?.[pick.round]?.matchupsPublished) return false
  const seedMatch = matches.find(m => m.round === pick.round && m.match_number === pick.match_number)
  if (!seedMatch) return false
  // Matchups published but a slot still empty -> nothing authoritative to check.
  if (!seedMatch.team_one_id && !seedMatch.team_two_id) return false
  return pick.teamId !== seedMatch.team_one_id && pick.teamId !== seedMatch.team_two_id
}

function computeLiveStats(picks, matches, bracket, officialPoolTiebreaks, roundStates = null) {
  if (bracket?.format === 'pool_then_sunday') {
    return computeLiveStatsPool(picks, matches, officialPoolTiebreaks)
  }
  let potentialPoints = 0
  let championTeamId = null
  let championshipRound = -1

  const isTeamEliminatedForStats = (teamId, pickRound) => {
    if (!teamId || pickRound === 0) return false
    for (const [_matchId, pick] of Object.entries(picks)) {
      if (!pick || pick.round >= pickRound) continue
      if (pick.teamId === teamId && pick.winner_team_id && pick.winner_team_id !== teamId) {
        return true
      }
    }
    return false
  }

  Object.values(picks).forEach(pick => {
    if (pick?.teamId && pick.round !== undefined) {
      const isTeamEliminated = isTeamEliminatedForStats(pick.teamId, pick.round)
      if (!pick.winner_team_id && !isTeamEliminated && !isPickOrphaned(pick, matches, roundStates)) {
        potentialPoints += getPointsForRound(pick.round)
      }
      if (pick.round > championshipRound && pick.teamId) {
        championshipRound = pick.round
        championTeamId = pick.teamId
      }
    }
  })

  let championshipRoundNum = -1
  let semifinalRoundNum = -1

  if (matches.length > 0) {
    const round1Matches = matches.filter(m => m.round === 1).length
    if (round1Matches > 0) {
      const totalRounds = Math.ceil(Math.log2(round1Matches)) + 1
      championshipRoundNum = totalRounds
      semifinalRoundNum = totalRounds - 1
    }
  }

  let hasChampionPick = false
  if (championshipRoundNum > 0 && semifinalRoundNum > 0) {
    const semifinalPicks = Object.values(picks).filter(pick =>
      pick?.round === semifinalRoundNum && pick?.teamId
    )
    const championshipPicks = Object.values(picks).filter(pick =>
      pick?.round === championshipRoundNum && pick?.teamId
    )
    hasChampionPick = semifinalPicks.length === 2 && championshipPicks.length === 1
  }

  let championWon = false
  if (hasChampionPick && championTeamId && championshipRoundNum > 0) {
    const champPick = Object.values(picks).find(p => p?.round === championshipRoundNum)
    if (champPick?.winner_team_id && champPick.winner_team_id === championTeamId) {
      championWon = true
    }
  }

  return {
    potentialPoints,
    hasChampionPick,
    championTeamId: hasChampionPick ? championTeamId : null,
    championWon,
  }
}

function computeMatchCellInfo(bracketStructure, matches, picks, getMatchTeamsFn) {
  const cellInfo = new Map()

  bracketStructure.forEach(round => {
    round.matches.forEach(match => {
      const { team1Id: actualTeam1Id, team2Id: actualTeam2Id } = getMatchTeamsFn(match, bracketStructure, matches, picks)

      let userPickedTeam1Id = actualTeam1Id
      let userPickedTeam2Id = actualTeam2Id

      if (match.round > 0) {
        let prevMatch1 = null
        let prevMatch2 = null
        if (match.round === 1) {
          prevMatch1 = matches.find(m =>
            m.round === 0 &&
            m.feeds_into_round === match.round &&
            m.feeds_into_match_number === match.match_number &&
            m.feeds_into_slot === 1
          )
          prevMatch2 = matches.find(m =>
            m.round === 0 &&
            m.feeds_into_round === match.round &&
            m.feeds_into_match_number === match.match_number &&
            m.feeds_into_slot === 2
          )
        } else {
          const prevRoundData = bracketStructure.find(r => r.roundNum === match.round - 1)
          if (prevRoundData) {
            const match1Index = (match.match_number * 2) - 2
            const match2Index = (match.match_number * 2) - 1
            prevMatch1 = prevRoundData.matches[match1Index]
            prevMatch2 = prevRoundData.matches[match2Index]
          }
        }

        if (prevMatch1) {
          const prevPick1 = picks[prevMatch1.match_id]
          if (prevPick1?.teamId) userPickedTeam1Id = prevPick1.teamId
        }

        if (prevMatch2) {
          const prevPick2 = picks[prevMatch2.match_id]
          if (prevPick2?.teamId) userPickedTeam2Id = prevPick2.teamId
        }
      }

      const cell2Visible = actualTeam1Id && userPickedTeam1Id && actualTeam1Id !== userPickedTeam1Id
      const cell3Visible = actualTeam2Id && userPickedTeam2Id && actualTeam2Id !== userPickedTeam2Id

      cellInfo.set(match.match_id, { cell2Visible, cell3Visible })
    })
  })

  return cellInfo
}

// A round-locked bracket authors each round's matchups (MLP playoffs: the bye
// teams choose their quarterfinal opponent, the quarterfinal winners choose the
// semis). Until the league PUBLISHES a round's matchups its participants are
// genuinely unknown, so the picks view must show TBD rather than optimistically
// advancing the previous round's winners into it. The lone exception is the
// final round: with only the two semifinal winners left there is no re-seeding,
// so it fills in directly from the previous round exactly like a normal bracket.
// (Round 1 always carries its real seeded teams, and legacy brackets with no
// round state are never affected.)
function isMatchupsPendingRound(roundStates, roundNum, bracketStructure) {
  if (!roundStates) return false
  if (roundNum <= 1) return false
  if (roundStates[roundNum]?.matchupsPublished) return false
  const finalRound = (bracketStructure || []).reduce((max, r) => Math.max(max, r.roundNum), 0)
  if (roundNum === finalRound) return false
  return true
}

// Pure helper for getMatchTeams (used by computeMatchCellInfo and as store method)
function getMatchTeamsPure(match, bracketStructure, matches, picks, isViewingOfficial, getMatchWinnerTeamIdFn, roundStates = null) {
  if (match.round === 0) {
    return { team1Id: match.team_one_id, team2Id: match.team_two_id }
  }

  // Multi-week bracket: when the league AUTHORS a round's matchups rather than
  // them following from bracket position (MLP playoffs -- the bye teams pick
  // their quarterfinal opponent from the first-round winners), the seed row is
  // the truth and there is nothing to derive. Everyone picks the real field,
  // including users whose previous-round picks busted.
  if (roundStates?.[match.round]?.matchupsPublished) {
    const seedMatch = matches.find(m => m.round === match.round && m.match_number === match.match_number)
    return {
      team1Id: seedMatch?.team_one_id ?? match.team_one_id ?? null,
      team2Id: seedMatch?.team_two_id ?? match.team_two_id ?? null,
    }
  }

  // Authored round whose matchups aren't published yet -> both slots are TBD.
  if (isMatchupsPendingRound(roundStates, match.round, bracketStructure)) {
    return { team1Id: null, team2Id: null }
  }

  const pqForSlot1 = matches.find(m =>
    m.round === 0 &&
    m.feeds_into_round === match.round &&
    m.feeds_into_match_number === match.match_number &&
    m.feeds_into_slot === 1
  )
  const pqForSlot2 = matches.find(m =>
    m.round === 0 &&
    m.feeds_into_round === match.round &&
    m.feeds_into_match_number === match.match_number &&
    m.feeds_into_slot === 2
  )

  let team1Id
  if (pqForSlot1) {
    team1Id = getMatchWinnerTeamIdFn(pqForSlot1, picks, isViewingOfficial)
  } else if (match.round === 1) {
    team1Id = match.team_one_id
  } else {
    const prevRoundNum = match.round - 1
    const prevRoundData = bracketStructure.find(r => r.roundNum === prevRoundNum)
    if (prevRoundData && prevRoundData.matches) {
      const match1Index = (match.match_number * 2) - 2
      const feedMatch1 = prevRoundData.matches[match1Index]
      team1Id = feedMatch1 ? getMatchWinnerTeamIdFn(feedMatch1, picks, isViewingOfficial) : null
    } else {
      team1Id = match.team_one_id
    }
  }

  let team2Id
  if (pqForSlot2) {
    team2Id = getMatchWinnerTeamIdFn(pqForSlot2, picks, isViewingOfficial)
  } else if (match.round === 1) {
    team2Id = match.team_two_id
  } else {
    const prevRoundNum = match.round - 1
    const prevRoundData = bracketStructure.find(r => r.roundNum === prevRoundNum)
    if (prevRoundData && prevRoundData.matches) {
      const match2Index = (match.match_number * 2) - 1
      const feedMatch2 = prevRoundData.matches[match2Index]
      team2Id = feedMatch2 ? getMatchWinnerTeamIdFn(feedMatch2, picks, isViewingOfficial) : null
    } else {
      team2Id = match.team_two_id
    }
  }

  return { team1Id, team2Id }
}

function getMatchWinnerTeamIdPure(match, picks, isViewingOfficial) {
  if (!match) return null
  const matchPick = picks[match.match_id]
  if (isViewingOfficial) {
    return matchPick ? (matchPick.teamId || null) : null
  }
  if (matchPick?.winner_team_id) return matchPick.winner_team_id
  if (matchPick?.teamId) return matchPick.teamId
  return null
}

function buildPoolStructure(state, picks, officialEntryView = false) {
  const { matches, poolRosters, teams, poolTiebreaks, officialPoolTiebreaks } = state
  const teamById = new Map(teams.map(t => [t.id, t]))

  const buildPool = (poolId) => {
    const roster = poolRosters.find(p => p.pool_id === poolId)
    const poolTeams = (roster?.teams || []).map(t => ({
      id: t.id,
      pool_seed: t.pool_seed,
      players: t.players || [],
    }))
    const poolRound = poolId === 'A' ? 1 : 2
    const poolMatches = matches
      .filter(m => m.match_kind === 'pool' && m.round === poolRound)
      .sort((a, b) => a.match_number - b.match_number)
    return { pool_id: poolId, teams: poolTeams, matches: poolMatches }
  }

  const poolA = buildPool('A')
  const poolB = buildPool('B')

  const picksByKey = new Map()
  Object.values(picks).forEach(p => {
    if (p?.round != null && p?.match_number != null && p.teamId) {
      picksByKey.set(`${p.round}:${p.match_number}`, {
        bracket_team_id: p.teamId,
      })
    }
  })

  // Explicit-advancer model (Phase 2, owner 2026-05-29). Two orderings come out
  // of the same picks:
  //  - Record order (stable: wins desc, then id) — drives the Projected
  //    Standings box, so it reads as a fixed reference and does NOT shuffle when
  //    the user reorders who advances (owner 2026-05-29).
  //  - Authoritative order — the user's explicit advancer order in poolTiebreaks
  //    (tapped/ranked teams take positions 1..k; any unplaced team falls in
  //    below by record). This drives who fills the Finals slots and which teams
  //    are flagged as advancing. Replaces the old derived-tiebreak math.
  const recordA = computePoolStandings(poolA.teams, poolA.matches, picksByKey, [])
  const recordB = computePoolStandings(poolB.teams, poolB.matches, picksByKey, [])
  const advancersA = computePoolStandings(poolA.teams, poolA.matches, picksByKey, poolTiebreaks?.A || [], { authoritative: true })
  const advancersB = computePoolStandings(poolB.teams, poolB.matches, picksByKey, poolTiebreaks?.B || [], { authoritative: true })

  // The set of teams that actually advance (top N of the authoritative order),
  // so the standings box can flag advancers without adopting their order.
  const advancingSet = (advancers, pool) =>
    new Set(advancers.slice(0, Math.min(4, pool.teams.length)).map(s => s.bracket_team_id))
  const advancingIdsA = advancingSet(advancersA, poolA)
  const advancingIdsB = advancingSet(advancersB, poolB)

  const allPoolPicked = (pool) => pool.matches.length > 0
    && pool.matches.every(m => picksByKey.has(`${m.round}:${m.match_number}`))
  const picksDoneA = allPoolPicked(poolA)
  const picksDoneB = allPoolPicked(poolB)

  // Readiness drives whether the Finals slots populate. Ordering is always
  // authoritative, so there is never an unresolved tie to gate on.
  //  - Official results (admin): require the advancers to be explicitly tapped
  //    — recording fact, must be deliberate.
  //  - Player / read-only view: ready once every pool match is picked. The
  //    advancer order is pre-filled from record and adjustable, so a valid
  //    top-4 always exists without forcing a tap.
  const advancersNeeded = (pool) => Math.min(4, pool.teams.length)
  const advancersChosen = (poolId, pool) => (poolTiebreaks?.[poolId]?.length || 0) >= advancersNeeded(pool)

  const ambiguityA = false
  const ambiguityB = false
  let readyA, readyB
  if (officialEntryView) {
    readyA = advancersChosen('A', poolA)
    readyB = advancersChosen('B', poolB)
  } else {
    readyA = picksDoneA
    readyB = picksDoneB
  }

  const sundayMatches = matches
    .filter(m => m.match_kind === 'sunday')
    .sort((a, b) => a.sunday_seed_index - b.sunday_seed_index)

  // Finals participants follow the authoritative advancer order, and are only
  // known once BOTH pools are ready (all picks made; advancers chosen on the
  // official path). Before that, keep the slots null so they read "Make pool
  // picks" / "Choose who advances" rather than implying a set matchup.
  const bothReady = readyA && readyB
  const derived = bothReady ? deriveSundayMatchups(advancersA, advancersB) : []

  // Official finalists overlay: when viewing a non-official entry that has
  // official results, derive the ACTUAL Sunday participants from the official
  // advancer order + official pool winners, so each Finals card can show the
  // official matchup alongside the user's predicted one. Uses the same
  // authoritative computePoolStandings + deriveSundayMatchups as the user path.
  let officialDerived = []
  if (
    !officialEntryView
    && officialPoolTiebreaks
    && ((officialPoolTiebreaks.A?.length || 0) > 0 || (officialPoolTiebreaks.B?.length || 0) > 0)
  ) {
    const officialPicksByKey = new Map()
    Object.values(picks).forEach(p => {
      if (p?.round != null && p?.match_number != null && p.winner_team_id) {
        officialPicksByKey.set(`${p.round}:${p.match_number}`, { bracket_team_id: p.winner_team_id })
      }
    })
    const offA = computePoolStandings(poolA.teams, poolA.matches, officialPicksByKey, officialPoolTiebreaks.A || [], { authoritative: true })
    const offB = computePoolStandings(poolB.teams, poolB.matches, officialPicksByKey, officialPoolTiebreaks.B || [], { authoritative: true })
    officialDerived = deriveSundayMatchups(offA, offB)
  }

  const sunday = sundayMatches.map(m => {
    const pair = derived.find(d => d.sunday_seed_index === m.sunday_seed_index)
    const teamA = pair ? teamById.get(pair.team_a_id) : null
    const teamB = pair ? teamById.get(pair.team_b_id) : null
    const offPair = officialDerived.find(d => d.sunday_seed_index === m.sunday_seed_index)
    const officialTeamA = offPair ? teamById.get(offPair.team_a_id) : null
    const officialTeamB = offPair ? teamById.get(offPair.team_b_id) : null
    return {
      ...m,
      teamA,
      teamB,
      officialTeamA,
      officialTeamB,
      isReady: !!(bothReady && teamA && teamB),
    }
  })

  // hasTie: any same-wins group of 2+ teams. Drives the "tied" label in the
  // standings box (record-order independent — it's a wins grouping).
  const hasTieA = recordA.some(s => s.tied_group_size > 1)
  const hasTieB = recordB.some(s => s.tied_group_size > 1)

  return {
    advancerMode: true,
    pools: [
      { ...poolA, standings: recordA, advancingIds: advancingIdsA, ready: readyA, picksDone: picksDoneA, hasAmbiguity: ambiguityA, hasTie: hasTieA },
      { ...poolB, standings: recordB, advancingIds: advancingIdsB, ready: readyB, picksDone: picksDoneB, hasAmbiguity: ambiguityB, hasTie: hasTieB },
    ],
    sunday,
  }
}

// ─── Store ───

const useBracketStore = create((set, get) => ({
  // ── External dependencies (passed via initialize) ──
  _navigate: null,
  _notify: null,
  _user: null,
  _searchParams: null,
  _setSearchParams: null,

  // ── Core data state ──
  tournament: null,
  bracket: null,
  tournamentBrackets: [],
  matches: [],
  teams: [],
  poolRosters: [],
  picks: {},
  originalPicks: {},
  loading: true,
  loadingPicks: false,
  error: null,
  userEntries: [],
  userEntriesLoaded: false,
  selectedEntryId: null,
  userEntry: null,
  officialEntryId: null,
  entryOwnerFirstName: null,
  // Full profile row of the viewed entry's owner (first/full name + avatar
  // fields) when viewing someone else's entry; null on own/official entries.
  entryOwnerProfile: null,
  // Rounds whose picks are hidden from this viewer (another player's entry,
  // round not yet locked+closed). Drives the "Hidden until picks close" cell
  // state so a masked round doesn't read as "made no picks" (ticket 5dacdb9f).
  viewerHiddenRounds: [],

  // ── Join bracket modal ──
  showJoinModal: false,
  userGroups: [],
  availableGroups: [],
  selectedGroupId: 'global',
  joining: false,

  // ── Missing picks modal ──
  showMissingPicksModal: false,
  missingPicks: [],

  // ── Banner ──
  isLockedBannerDismissed: false,

  // ── Group management ──
  ownedGroups: [],
  loadingOwnedGroups: false,

  // ── Leaderboard ──
  viewingLeaderboard: false,
  leaderboard: [],
  leaderboardLoading: false,
  leaderboardGroupFilter: 'global',

  // ── UI state ──
  viewMode: 'staggered',
  isMobile: false,
  bracketId: null,

  // ── Scroll state (read by multiple components, managed by useBracketScroll) ──
  mobileRoundIndex: 0,
  displayRoundIndex: 0,
  showBackToTop: false,
  // Whether the view-toggle bar's inline Save button is on screen (true also
  // when the button isn't rendered at all). The floating Save FAB in
  // BracketPicks shows only while it's NOT, so the two render as one button
  // that relocates on scroll.
  inlineSaveInView: true,

  // ── Derived state ──
  isAdmin: false,
  entryIdFromUrl: null,
  isViewingOfficial: false,
  isEditingOfficial: false,
  // True when the admin reached this bracket via an admin entry point —
  // either Edit Official Results (mode=editOfficial) or View Bracket from
  // the seed editor (mode=adminView). Used by breadcrumb / back-nav so a
  // backward step lands in the admin tournament edit page rather than the
  // player-flow tournament page.
  isAdminBracketContext: false,
  isNewEntryMode: false,
  isViewingEntry: false,
  isOwnEntry: false,
  canEditPicks: false,
  // null for legacy brackets; { [round]: { status, deadline, label, matchupsPublished } }
  // for multi-week brackets that lock round by round.
  roundStates: null,
  usesRoundLocks: false,
  bracketStructure: [],
  poolStructure: null,
  poolTiebreaks: {},
  // The OFFICIAL entry's advancer order, loaded when viewing a non-official
  // entry on a locked/complete pool bracket so the Finals cards can overlay the
  // official finalists next to the user's predicted ones.
  officialPoolTiebreaks: null,
  // Pool tiebreaker order is an unsaved edit until the bracket Save persists
  // it (folded into hasPicksChanged). Reset on load and after save.
  tiebreaksDirty: false,
  bracketRoundCount: 0,
  hasPicksChanged: false,
  hasAnyPicks: false,
  userSaveLabel: 'Edit picks',
  liveStats: { potentialPoints: 0, hasChampionPick: false, championTeamId: null, championWon: false },
  matchCellInfo: new Map(),

  // ── Setters for simple state ──
  setPoolTiebreak: (poolId, rankedTeamIds) => {
    // Snapshot Finals matchups before the reorder. Changing a tiebreaker
    // reseeds a pool, which can change who plays whom in the Finals. Any
    // Finals pick whose matchup composition changes is stale and must be
    // re-picked (owner 2026-05-21: re-pick when EITHER team in the matchup
    // changes). Matchups that are unchanged keep their pick.
    const beforeMatchups = new Map(
      (get().poolStructure?.sunday || []).map(m => [
        m.sunday_seed_index,
        [m.teamA?.id || null, m.teamB?.id || null],
      ])
    )

    const next = { ...(get().poolTiebreaks || {}) }
    if (rankedTeamIds && rankedTeamIds.length > 0) next[poolId] = rankedTeamIds
    else delete next[poolId]
    // Mark the bracket dirty rather than auto-saving here. Auto-saving on each
    // confirm raced (confirming Pool A then B fired two writes that could land
    // out of order, losing B), and it left the Save button hidden after a
    // tiebreaker change. tiebreaksDirty folds into hasPicksChanged so the
    // bracket Save button appears, and handleSave is the single writer.
    set({ poolTiebreaks: next, tiebreaksDirty: true })
    get().recomputeDerivedState()

    // Reconcile Finals picks against the new matchups.
    const picks = { ...get().picks }
    let cleared = false
    ;(get().poolStructure?.sunday || []).forEach(m => {
      const before = beforeMatchups.get(m.sunday_seed_index) || [null, null]
      const after = [m.teamA?.id || null, m.teamB?.id || null]
      const sameMatchup =
        (before[0] === after[0] && before[1] === after[1]) ||
        (before[0] === after[1] && before[1] === after[0])
      if (sameMatchup) return
      const p = picks[m.match_id]
      if (p?.teamId) {
        picks[m.match_id] = {
          teamId: null, teamName: null, seed: null,
          round: p.round, match_number: p.match_number,
          winner_team_id: p.winner_team_id, official_winner_name: p.official_winner_name,
          isCorrect: false,
        }
        cleared = true
      }
    })
    if (cleared) {
      set({ picks })
      get().recomputeDerivedState()
    }
  },

  setShowJoinModal: (v) => set({ showJoinModal: v }),
  setSelectedGroupId: (v) => set({ selectedGroupId: v }),
  setShowMissingPicksModal: (v) => set({ showMissingPicksModal: v }),
  setMissingPicks: (v) => set({ missingPicks: v }),
  setIsLockedBannerDismissed: (v) => set({ isLockedBannerDismissed: v }),
  setViewingLeaderboard: (v) => set({ viewingLeaderboard: v }),
  setLeaderboardGroupFilter: (v) => set({ leaderboardGroupFilter: v }),
  setViewMode: (v) => set({ viewMode: v }),
  setIsMobile: (v) => set({ isMobile: v }),
  setMobileRoundIndex: (v) => set({ mobileRoundIndex: v }),
  setDisplayRoundIndex: (v) => set({ displayRoundIndex: v }),
  setShowBackToTop: (v) => set({ showBackToTop: v }),
  setInlineSaveInView: (v) => set({ inlineSaveInView: v }),

  // Returns the index of the highest round (inside bracketStructure) whose official pick
  // has a winner_team_id set. -1 when no winners have been recorded yet.
  computeLatestUpdatedRoundIndex: () => {
    const { bracketStructure, picks } = get()
    if (!bracketStructure?.length) return -1
    let bestIndex = -1
    bracketStructure.forEach((round, idx) => {
      const hasWinner = round.matches?.some((m) => picks?.[m.match_id]?.winner_team_id)
      if (hasWinner && idx > bestIndex) bestIndex = idx
    })
    return bestIndex
  },

  // Auto-jump to the latest updated round; no-op when none found. Used when the user
  // arrives from a notification that results were updated.
  jumpToLatestUpdatedRound: () => {
    const idx = get().computeLatestUpdatedRoundIndex()
    if (idx < 0) return false
    set({ displayRoundIndex: idx, mobileRoundIndex: idx })
    return true
  },

  // DUB-102: which round to open at when (re)mounting the bracket. Intent
  // is "land where progress currently is" — not strictly the lowest empty
  // round. The lowest-empty rule was wrong in the common case where a
  // bracket has a pre-qual round (R0) the user skipped: it would dump them
  // back on R0 even when R1 was fully picked.
  //
  // The "progress" signal differs by mode:
  //   - Player making picks (canEditPicks && !isEditingOfficial): the user's
  //     own picks (teamId). Lands them on the round they were last working
  //     on so they can keep filling out their bracket.
  //   - Admin editing official results (isEditingOfficial): official winners
  //     (winner_team_id). Lands the admin on the next round to enter results
  //     for.
  //   - Player viewing their own bracket post-lock, or viewing someone
  //     else's bracket (canEditPicks=false): official winners
  //     (winner_team_id). Lands on the currently-playing round so the user
  //     sees how their picks compare to live results, not the championship
  //     round they filled out months ago.
  //
  // Algorithm (using the appropriate progress signal):
  //   1. Find the highest round index H that has any progress recorded.
  //   2. If round H is fully filled, land on H+1.
  //   3. If round H is partial, land on H.
  //   4. If no round has any progress (fresh bracket / tournament not
  //      started), land on 0.
  computeInitialRoundIndex: () => {
    const { bracketStructure, picks, isEditingOfficial, canEditPicks, roundStates, usesRoundLocks } = get()
    if (!bracketStructure?.length) return 0
    const useUserPicks = canEditPicks && !isEditingOfficial
    const hasProgress = (m) => useUserPicks
      ? !!picks?.[m.match_id]?.teamId
      : !!picks?.[m.match_id]?.winner_team_id

    let highestWithProgress = -1
    for (let i = 0; i < bracketStructure.length; i++) {
      if (bracketStructure[i].matches?.some(hasProgress)) highestWithProgress = i
    }
    if (highestWithProgress < 0) return 0

    const roundComplete = bracketStructure[highestWithProgress].matches?.every(hasProgress)
    if (roundComplete && highestWithProgress < bracketStructure.length - 1) {
      const nextRoundIdx = highestWithProgress + 1
      const nextRound = bracketStructure[nextRoundIdx]
      const nextRoundNum = nextRound?.roundNum
      const nextRoundHasProgress = nextRound?.matches?.some(hasProgress)
      const nextRoundEditable = usesRoundLocks
        ? (isEditingOfficial ? roundStates?.[nextRoundNum]?.status === 'locked' : (canEditPicks && roundStates?.[nextRoundNum]?.status === 'open'))
        : canEditPicks

      if (nextRoundHasProgress || nextRoundEditable) {
        return nextRoundIdx
      }
    }
    return highestWithProgress
  },

  // ── Recompute all derived state ──
  recomputeDerivedState: () => {
    const state = get()
    const { matches, picks, originalPicks, bracket, _user, _searchParams, userEntriesLoaded, userEntries, userEntry } = state
    const entryIdFromUrl = state.entryIdFromUrl

    const isAdmin = _user?.role === 'admin'
    const shouldFallbackToOfficial = !entryIdFromUrl
      && userEntriesLoaded
      && bracket?.status === 'locked'
      && userEntries.length === 0
    const isViewingOfficial = entryIdFromUrl === 'official' || shouldFallbackToOfficial
    // Admin edit-official mode is only entered via an explicit admin entry
    // point (the "Enter Results" / "Edit Official Results" buttons), which
    // tag the URL with mode=editOfficial. Without that flag, an admin who
    // happens to be viewing ?entryId=official (e.g. via the locked-bracket
    // fallback while browsing the user flow) stays read-only.
    const modeParam = _searchParams?.get?.('mode')
    // Official results stay editable after a bracket auto-completes, not just
    // while 'locked'. pool_then_sunday brackets auto-complete on the tournament
    // end_date (cron), which lands before the admin has entered the Sunday
    // Finals winners — gating on 'locked' alone locked admins out of finishing
    // official results (DUB-187/188). save_entry mirrors this on the DB side.
    // A round-locked bracket stays 'open' across the whole multi-week run, so
    // the bracket-level locked/complete requirement cannot apply to it; the
    // per-round check (a round must be 'locked') is what gates official edits.
    const isEditingOfficial = isAdmin
      && isViewingOfficial
      && (bracket?.rounds?.length > 0
        ? bracket?.status !== 'draft'
        : ['locked', 'complete'].includes(bracket?.status))
      && modeParam === 'editOfficial'
    // adminView is the read-only counterpart: admin clicked "View Bracket"
    // from the seed editor. No editing rights, but breadcrumb/back-nav should
    // still walk back into /admin land rather than the player tournament page.
    const isAdminBracketContext = isAdmin
      && (isEditingOfficial || modeParam === 'adminView')
    const isNewEntryMode = !entryIdFromUrl && !shouldFallbackToOfficial
    const isViewingEntry = !!entryIdFromUrl && entryIdFromUrl !== 'official'
    const isOwnEntry = isNewEntryMode || (isViewingEntry && userEntry?.user_id === _user?.id)

    // Multi-week brackets gate editing per round, not per bracket. The bracket
    // itself stays 'open' for the whole run so entries can still be created
    // (a user who misses week 1 joins at the quarterfinals and forfeits the
    // round-1 points), and each round decides who may write to it.
    const roundStates = buildRoundStates(bracket?.rounds)
    const usesRoundLocks = !!roundStates
    const hasOpenRound = usesRoundLocks
      && Object.values(roundStates).some(r => r.status === 'open')
    const hasLockedRound = usesRoundLocks
      && Object.values(roundStates).some(r => r.status === 'locked')

    // canEditPicks stays "is anything editable at all" -- it drives the save
    // FAB and the locked banner. Per-match gating goes through canEditRound.
    const canEditPicks = isViewingOfficial
      ? (usesRoundLocks
          ? (isEditingOfficial && hasLockedRound)
          : isEditingOfficial)
      : (usesRoundLocks
          ? (isOwnEntry && bracket?.status !== 'draft' && bracket?.status !== 'complete' && hasOpenRound)
          : (isOwnEntry && bracket?.status === 'open'))

    const bracketStructure = computeBracketStructure(matches, picks)
    const bracketRoundCount = computeBracketRoundCount(matches)
    // A pending tiebreaker reorder is a savable change even when the picks
    // themselves match what's saved, so it counts toward hasPicksChanged.
    const hasPicksChanged = computeHasPicksChanged(picks, originalPicks) || state.tiebreaksDirty
    const hasAnyPicks = Object.keys(picks).length > 0
    const userSaveLabel = computeUserSaveLabel(hasPicksChanged, isNewEntryMode, hasAnyPicks)
    const liveStats = computeLiveStats(picks, matches, bracket, state.officialPoolTiebreaks, roundStates)

    // For matchCellInfo we need getMatchTeams which depends on isViewingOfficial
    const getMatchTeamsFn = (match, bs, ms, ps) =>
      getMatchTeamsPure(match, bs, ms, ps, isViewingOfficial, getMatchWinnerTeamIdPure, roundStates)
    const matchCellInfo = computeMatchCellInfo(bracketStructure, matches, picks, getMatchTeamsFn)

    const poolStructure = bracket?.format === 'pool_then_sunday'
      ? buildPoolStructure(state, picks, isViewingOfficial)
      : null

    set({
      isAdmin,
      isViewingOfficial,
      isEditingOfficial,
      isAdminBracketContext,
      isNewEntryMode,
      isViewingEntry,
      isOwnEntry,
      canEditPicks,
      roundStates,
      usesRoundLocks,
      bracketStructure,
      poolStructure,
      bracketRoundCount,
      hasPicksChanged,
      hasAnyPicks,
      userSaveLabel,
      liveStats,
      matchCellInfo,
    })
  },

  // ── Helper functions ──

  getTeamInfo: (teamId) => {
    if (!teamId) return null
    return get().teams.find(t => t.id === teamId)
  },

  formatTeamName: (team) => {
    if (!team) return 'TBD'
    if (!team.players || team.players.length === 0) return 'TBD'
    if (team.players.length > 1) return team.players.map(p => p.name).join(' / ')
    return team.players[0].name
  },

  getPointsForRound,
  getRoundLabel,

  getMatchWinnerTeamId: (match) => {
    const { picks, isViewingOfficial } = get()
    return getMatchWinnerTeamIdPure(match, picks, isViewingOfficial)
  },

  getMatchTeams: (match) => {
    const { bracketStructure, matches, picks, isViewingOfficial, roundStates } = get()
    return getMatchTeamsPure(match, bracketStructure, matches, picks, isViewingOfficial, getMatchWinnerTeamIdPure, roundStates)
  },

  // Per-match editability for multi-week brackets. Returns canEditPicks
  // unchanged for legacy brackets, so existing call sites keep working.
  canEditRound: (roundNum) => {
    const { canEditPicks, roundStates, isEditingOfficial } = get()
    if (!canEditPicks) return false
    if (!roundStates) return true
    return isRoundEditable(roundStates, roundNum, isEditingOfficial)
  },

  // UI label for a round's state, e.g. the carousel badge.
  getRoundLockInfo: (roundNum) => {
    const { roundStates, isEditingOfficial } = get()
    if (!roundStates) return null
    const info = roundStates[roundNum] || { round: roundNum, status: 'pending', deadline: null, label: null, matchupsPublished: false }
    return { ...info, editable: isRoundEditable(roundStates, roundNum, isEditingOfficial) }
  },

  // True when this round's matchups are league-authored but not yet published,
  // so its slots should read TBD instead of carrying the previous round's
  // winners forward. The renderer uses this to skip the predicted-advancer
  // overlay and show TBD placeholders.
  isMatchupsPending: (roundNum) => {
    const { roundStates, bracketStructure } = get()
    return isMatchupsPendingRound(roundStates, roundNum, bracketStructure)
  },

  isTeamEliminatedInPreviousRounds: (teamId, currentMatch) => {
    const { bracketStructure, matches, picks } = get()
    if (!teamId || !currentMatch || currentMatch.round === 0) return false

    const prevRoundNum = currentMatch.round - 1
    const prevRoundData = bracketStructure.find(r => r.roundNum === prevRoundNum)
    if (!prevRoundData) return false

    const checkEliminated = get().isTeamEliminatedInPreviousRounds

    if (currentMatch.round === 1) {
      const pqMatches = matches.filter(m =>
        m.round === 0 &&
        m.feeds_into_round === currentMatch.round &&
        m.feeds_into_match_number === currentMatch.match_number
      )
      for (const pqMatch of pqMatches) {
        const pqPick = picks[pqMatch.match_id]
        if (pqPick?.teamId === teamId) {
          if (pqPick.winner_team_id && pqPick.winner_team_id !== teamId) return true
          if (checkEliminated(teamId, pqMatch)) return true
        }
      }
    }

    const match1Index = (currentMatch.match_number * 2) - 2
    const match2Index = (currentMatch.match_number * 2) - 1
    const prevMatches = [
      prevRoundData.matches[match1Index],
      prevRoundData.matches[match2Index],
    ].filter(Boolean)

    for (const prevMatch of prevMatches) {
      const prevPick = picks[prevMatch.match_id]
      if (prevPick?.teamId === teamId) {
        if (prevPick.winner_team_id && prevPick.winner_team_id !== teamId) return true
        if (checkEliminated(teamId, prevMatch)) return true
      }
    }

    return false
  },

  findNextAvailableMatch: (currentMatch) => {
    const { bracketStructure, picks } = get()
    const getMatchTeams = get().getMatchTeams
    if (!currentMatch || !bracketStructure.length) return null

    const currentRoundData = bracketStructure.find(r => r.roundNum === currentMatch.round)
    if (!currentRoundData) return null

    const currentMatchIndex = currentRoundData.matches.findIndex(
      m => m.match_number === currentMatch.match_number
    )

    // "Fillable" = both teams known AND not yet picked → safe to land on.
    // "Pending" = no pick yet (teams may or may not be known) → the round is
    // still incomplete. DUB-104: when upstream picks are missing, current-
    // round matches show null teams and aren't fillable, but they're still
    // pending — the user's mental model is "this round isn't done yet, don't
    // yank me forward."
    const isFillable = (m) => {
      const { team1Id, team2Id } = getMatchTeams(m)
      return (team1Id || team2Id) && !picks[m.match_id]?.teamId
    }
    const isPending = (m) => !picks[m.match_id]?.teamId

    // Stay inside the current round until every match in it is picked. Without
    // the wrap-around, picking the LAST match in a round forces the next-match
    // search to fall through to the next round even when earlier matches in
    // this round are still empty — which dumps the user at the top of the
    // next round mid-fill-out and breaks the "fill round, then advance" flow.
    for (let i = currentMatchIndex + 1; i < currentRoundData.matches.length; i++) {
      if (isFillable(currentRoundData.matches[i])) return currentRoundData.matches[i]
    }
    for (let i = 0; i < currentMatchIndex; i++) {
      if (isFillable(currentRoundData.matches[i])) return currentRoundData.matches[i]
    }

    // DUB-104: if the current round still has any pending match (even one
    // with null teams because upstream isn't filled), don't fall through to
    // the next round. The user has more work in this round; staying put is
    // better than jumping forward and disorienting them.
    if (currentRoundData.matches.some(isPending)) return null

    const currentRoundIndex = bracketStructure.findIndex(r => r.roundNum === currentMatch.round)
    for (let roundIdx = currentRoundIndex + 1; roundIdx < bracketStructure.length; roundIdx++) {
      const roundData = bracketStructure[roundIdx]
      for (const nextMatch of roundData.matches) {
        if (isFillable(nextMatch)) return nextMatch
      }
    }

    return null
  },

  // ── Data loading actions ──

  fetchBracketData: async () => {
    const { bracketId, isAdmin, _navigate, _notify, _user } = get()
    try {
      set({ loading: true, error: null })

      const data = await fetchBracketWithDetails(bracketId)

      if (!data) {
        set({ error: 'Bracket not found. It may have been deleted or you may not have permission to view it.' })
        _notify.error('Bracket not found')
        return
      }

      if (data.status === 'draft' && !isAdmin) {
        _notify.warning('This bracket is not yet available for viewing.')
        _navigate('/home')
        return
      }

      const divisionInfo = data.division || {}
      const tournamentInfo = data.tournament || {}
      const seeds = data.seeds || []

      const bracket = {
        id: data.id,
        tournamentId: data.tournament_id,
        divisionId: data.division_id,
        divisionName: divisionInfo.name,
        divisionGender: divisionInfo.gender,
        divisionTeamSize: divisionInfo.team_size,
        status: data.status,
        format: data.format || 'single_elim',
        // Empty for every legacy bracket; populated only for multi-week
        // brackets that opt into per-round locking.
        rounds: data.rounds || [],
      }
      const poolRosters = data.pool_rosters || []

      let tournamentBrackets = []
      try {
        const bracketsData = await fetchTournamentBracketsWithEntries(tournamentInfo.id)
        if (bracketsData && Array.isArray(bracketsData)) {
          tournamentBrackets = bracketsData.map(b => {
            const userEntryItem = b.bracket_entries?.find(e => e.user_id === _user?.id && e.group_id === null)
            return {
              id: b.id,
              divisionId: b.division_id,
              divisionName: b.divisions?.name || 'Unknown Division',
              divisionGender: b.divisions?.gender,
              divisionTeamSize: b.divisions?.team_size,
              status: b.status,
              hasUserEntry: !!userEntryItem,
              userEntryId: userEntryItem?.id,
            }
          })
        }
      } catch (bracketsError) {
        console.error('Error fetching tournament brackets:', bracketsError)
      }

      // Build teams array from seeds
      const teamsMap = new Map()
      seeds.forEach(seed => {
        const processTeam = (teamData) => {
          if (!teamData || teamsMap.has(teamData.id)) return
          let sortedPlayers = [...teamData.players]
          if (divisionInfo.gender === 'mixed' && sortedPlayers.length === 2) {
            sortedPlayers.sort((a, b) => {
              if (a.gender === 'female' && b.gender === 'male') return -1
              if (a.gender === 'male' && b.gender === 'female') return 1
              return 0
            })
          }
          teamsMap.set(teamData.id, {
            id: teamData.id,
            seed: teamData.seed,
            players: sortedPlayers,
          })
        }
        processTeam(seed.team_one)
        processTeam(seed.team_two)
      })

      const transformedMatches = seeds.map(seed => ({
        match_id: seed.id,
        round: seed.round,
        match_number: seed.match_number,
        team_one_id: seed.team_one?.id || null,
        team_two_id: seed.team_two?.id || null,
        feeds_into_round: seed.feeds_into_round,
        feeds_into_match_number: seed.feeds_into_match_number,
        feeds_into_slot: seed.feeds_into_slot,
        match_kind: seed.match_kind || 'bracket',
        pool_id: seed.pool_id || null,
        sunday_seed_index: seed.sunday_seed_index || null,
      }))

      // Pool rosters carry the team objects for pool play; merge them into
      // teamsMap so single getTeamInfo() works for both pool and bracket
      // teams.
      poolRosters.forEach(pool => {
        pool.teams?.forEach(t => {
          if (!teamsMap.has(t.id)) {
            teamsMap.set(t.id, {
              id: t.id,
              seed: t.pool_seed,
              players: t.players || [],
              pool_id: pool.pool_id,
            })
          }
        })
      })

      set({
        tournament: tournamentInfo,
        bracket,
        tournamentBrackets,
        matches: transformedMatches,
        teams: Array.from(teamsMap.values()),
        poolRosters,
        officialEntryId: data.official_entry_id || null,
      })

      get().recomputeDerivedState()
    } catch (err) {
      console.error('Error loading bracket:', err)
      set({ error: `An unexpected error occurred: ${err.message || 'Unknown error'}` })
      _notify.error('Failed to load bracket')
    } finally {
      set({ loading: false })
    }
  },

  loadUserEntries: async (userOverride = null) => {
    const { _user, bracketId, entryIdFromUrl, _setSearchParams, _searchParams } = get()
    const user = userOverride || _user
    const isAdmin = user?.role === 'admin'
    if (!user?.id) return

    // Helper: merge a change into the CURRENT URL search params so we don't
    // accidentally strip admin-context flags (mode=editOfficial) or any other
    // unrelated query param.
    const mergeParams = (patch) => {
      const base = _searchParams ? new URLSearchParams(_searchParams) : new URLSearchParams()
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null) base.delete(k)
        else base.set(k, v)
      })
      return base
    }

    try {
      set({ userEntriesLoaded: false })
      const entriesData = await fetchEntries(user.id, bracketId)
      const entries = entriesData || []
      set({ userEntries: entries })

      if (!entryIdFromUrl && entries.length > 0) {
        // Land on the user's own entry (admins included). Admins who want the
        // official view can click "Edit Official Results" or append
        // ?entryId=official explicitly.
        const globalEntry = entries.find(e => e.group_id === null)
        const entryToSelect = globalEntry || entries[0]
        if (entryToSelect) {
          _setSearchParams(mergeParams({ entryId: entryToSelect.id }), { replace: true })
          // Only load picks directly if matches are already available
          if (get().matches.length > 0) {
            await get().loadEntryPicks(entryToSelect.id)
          }
        }
      }

      // For locked brackets with no entries, fall back to official entry directly
      // rather than relying on the effect chain in BracketPicks.jsx. Applies to
      // admins too (owner 2026-06-09): an admin with no entry should see the
      // official read-only view like any user, not a blank page. The explicit
      // editOfficial flow sets ?entryId=official, so the !entryIdFromUrl guard
      // already keeps that flow out of this branch.
      if (!entryIdFromUrl && entries.length === 0) {
        const { bracket, officialEntryId, matches } = get()
        if (bracket?.status === 'locked' && officialEntryId) {
          _setSearchParams(mergeParams({ entryId: 'official' }), { replace: true })
          // Only load picks directly if matches are already available
          if (matches.length > 0) {
            await get().loadEntryPicks(officialEntryId)
          }
        }
      }

      let myGroups = []
      try {
        const groupsData = await fetchGroups(user.id)
        myGroups = groupsData || []
      } catch (groupsErr) {
        console.error('Error loading groups:', groupsErr)
      }
      set({ userGroups: myGroups })

      const entryGroupIds = entries.map(e => e.group_id)
      const hasGlobalEntry = entryGroupIds.includes(null)

      const available = []
      if (!hasGlobalEntry) available.push({ id: null, name: 'Global' })
      myGroups.forEach(group => {
        if (!entryGroupIds.includes(group.id)) available.push({ id: group.id, name: group.name })
      })
      set({ availableGroups: available })
    } catch (err) {
      console.error('Error loading user entries:', err)
      set({ userEntries: [] })
    } finally {
      set({ userEntriesLoaded: true })
      get().recomputeDerivedState()
    }
  },

  loadOwnedGroups: async () => {
    const { _user, bracketId } = get()
    if (!_user?.id || !bracketId) return
    try {
      set({ loadingOwnedGroups: true })
      const data = await fetchGroupsForBracket(bracketId, _user.id)
      set({ ownedGroups: data || [] })
    } catch (err) {
      console.error('Error loading owned groups:', err)
      set({ ownedGroups: [] })
    } finally {
      set({ loadingOwnedGroups: false })
    }
  },

  loadLeaderboard: async (groupId = null) => {
    const { bracketId, bracket } = get()
    if (!bracketId || !bracket) return
    try {
      set({ leaderboardLoading: true })
      const data = await fetchLeaderboardRankings({ groupId, bracketId })
      set({ leaderboard: data || [] })
    } catch (err) {
      console.error('Error loading bracket leaderboard:', err)
      set({ leaderboard: [] })
    } finally {
      set({ leaderboardLoading: false })
    }
  },

  loadEntryPicks: async (overrideEntryId = null) => {
    const state = get()
    const { isNewEntryMode, isViewingOfficial, isEditingOfficial, officialEntryId, selectedEntryId, isAdmin, bracketId, matches, _user } = state
    const getTeamInfo = state.getTeamInfo
    const formatTeamName = state.formatTeamName

    try {
      set({ loadingPicks: true })

      if (isNewEntryMode && !overrideEntryId) {
        set({ picks: {}, originalPicks: {}, userEntry: null, viewerHiddenRounds: [], loadingPicks: false })
        get().recomputeDerivedState()
        return
      }

      const entryIdToFetch = overrideEntryId || (isViewingOfficial ? officialEntryId : selectedEntryId)

      if (!entryIdToFetch) {
        if (isViewingOfficial && isAdmin && bracketId) {
          try {
            const existingEntry = await fetchOfficialEntry(bracketId)
            let officialId = null

            if (existingEntry) {
              officialId = existingEntry.id
              try {
                await updateBracketOfficialEntryId(bracketId, officialId)
              } catch (updateError) {
                console.error('Error updating bracket official_entry_id:', updateError)
              }
            } else if (isEditingOfficial) {
              // Only create a brand-new official entry when the admin
              // explicitly entered edit-official mode. An admin who merely
              // browsed to ?entryId=official via the locked-bracket fallback
              // should not silently create a blank official entry server-side
              // as a side effect of casual navigation.
              const createData = await createEntry(bracketId, true, null)
              officialId = createData.entry_id
            } else {
              // Viewing-only: no official entry exists yet — leave picks
              // empty so the page renders the bracket seeds without any
              // official result overlay.
              set({ picks: {}, originalPicks: {}, userEntry: null, viewerHiddenRounds: [] })
              get().recomputeDerivedState()
              return
            }

            set({ officialEntryId: officialId })
            get().recomputeDerivedState()
            await get().loadEntryPicks(officialId)
            return
          } catch (err) {
            console.error('Error handling official entry:', err)
            set({ picks: {}, originalPicks: {}, userEntry: null, viewerHiddenRounds: [] })
            get().recomputeDerivedState()
            return
          }
        }

        set({ picks: {}, originalPicks: {}, userEntry: null, viewerHiddenRounds: [] })
        get().recomputeDerivedState()
        return
      }

      const entryData = await fetchEntryById(entryIdToFetch)

      if (!entryData) {
        set({ picks: {}, originalPicks: {}, userEntry: null, viewerHiddenRounds: [] })
        get().recomputeDerivedState()
        return
      }

      // Owner 2026-05-21: a player can't view another player's picks until the
      // bracket is locked (picks closed). Backstop for direct URL access — the
      // profile UI already hides the link pre-lock.
      // DUB-150/151/152: admins are NOT exempt. The rule must apply to everyone
      // in the browse flow (an admin could previously reach picks via the
      // leaderboard/direct URL while the profile path blocked them). Admin
      // official-results editing is unaffected: the official entry has a NULL
      // user_id, so isOtherUsersEntry is false there.
      // Only gate when we actually KNOW the viewer (_user.id present) and it's
      // a different user — otherwise a race where _user hasn't hydrated yet
      // would treat the user's OWN entry as someone else's and clear it.
      const isOtherUsersEntry = !!_user?.id && entryData.user_id && entryData.user_id !== _user.id
      const roundStatesForEntry = buildRoundStates(state.bracket?.rounds)
      const usesRoundLocksForEntry = !!roundStatesForEntry
      const picksArePublic = usesRoundLocksForEntry
        ? Object.values(roundStatesForEntry).some(r => r.status === 'locked' || r.status === 'complete')
        : ['locked', 'complete'].includes(state.bracket?.status)
      const bracketUnlocked = !picksArePublic
      if (isOtherUsersEntry && bracketUnlocked) {
        state._notify?.warning?.("You can view other players' picks once the bracket locks.")
        let ownEntry = null
        try {
          const mine = state.userEntries?.length
            ? state.userEntries
            : (await fetchEntries(_user.id, state.bracketId)) || []
          ownEntry = mine.find(e => e.group_id === null) || mine[0] || null
        } catch (err) {
          console.error('Error falling back to own entry:', err)
        }
        if (state._setSearchParams && state._searchParams) {
          const next = new URLSearchParams(state._searchParams)
          if (ownEntry?.id) next.set('entryId', ownEntry.id)
          else next.delete('entryId')
          state._setSearchParams(next, { replace: true })
        }
        set({ picks: {}, originalPicks: {}, userEntry: null, entryOwnerFirstName: null, entryOwnerProfile: null, viewerHiddenRounds: [], loadingPicks: false })
        get().recomputeDerivedState()
        if (ownEntry?.id) await get().loadEntryPicks(ownEntry.id)
        return
      }

      if (isViewingOfficial) {
        set({ userEntry: null, entryOwnerFirstName: null, entryOwnerProfile: null, viewerHiddenRounds: [] })
      } else {
        let ownerProfile = null
        if (entryData.user_id && entryData.user_id !== _user?.id) {
          try {
            ownerProfile = await fetchUserFirstName(entryData.user_id)
          } catch (err) {
            console.error('Error fetching entry owner name:', err)
          }
        }
        // Rounds this viewer can't see picks for. Mirrors the isPickRoundLocked
        // rule below (and the server-side masking): another player's rounds
        // stay hidden until locked/complete. The renderer shows these as
        // "Hidden until picks close" instead of blank (ticket 5dacdb9f).
        const hiddenRounds = (isOtherUsersEntry && usesRoundLocksForEntry)
          ? Object.entries(roundStatesForEntry)
              .filter(([, r]) => r.status !== 'locked' && r.status !== 'complete')
              .map(([roundNum]) => Number(roundNum))
          : []
        set({
          userEntry: entryData,
          entryOwnerFirstName: ownerProfile?.first_name || null,
          entryOwnerProfile: ownerProfile || null,
          viewerHiddenRounds: hiddenRounds,
        })
      }

      // Hydrate pool tiebreaker rankings (winner-only mode); empty object if
      // none have been saved yet. Freshly loaded = not dirty.
      set({ poolTiebreaks: entryData.pool_tiebreaks || {}, tiebreaksDirty: false })

      const entryPicks = entryData.picks || []
      const loadedPicks = {}

      entryPicks.forEach(pick => {
        const match = matches.find(m => m.round === pick.round && m.match_number === pick.match_number)
        const matchId = match?.match_id || `placeholder-r${pick.round}-m${pick.match_number}`

        // When viewing another player's entry on a multi-week bracket, hide picks for rounds that are still open or pending.
        const isPickRoundLocked = !isOtherUsersEntry || !usesRoundLocksForEntry || (
          roundStatesForEntry?.[pick.round]?.status === 'locked' ||
          roundStatesForEntry?.[pick.round]?.status === 'complete'
        )

        const selectedTeamId = isPickRoundLocked ? pick.bracket_team_id : null
        const selectedTeam = selectedTeamId ? getTeamInfo(selectedTeamId) : null
        const officialWinnerId = pick.official_bracket_team_id
        const officialWinnerTeam = officialWinnerId ? getTeamInfo(officialWinnerId) : null
        const isCorrect = !isViewingOfficial && selectedTeamId && selectedTeamId === officialWinnerId

        loadedPicks[matchId] = {
          teamId: selectedTeamId || null,
          teamName: selectedTeam ? formatTeamName(selectedTeam) : null,
          seed: selectedTeam?.seed || null,
          winner_team_id: officialWinnerId || null,
          official_winner_name: officialWinnerTeam ? formatTeamName(officialWinnerTeam) : null,
          round: pick.round,
          match_number: pick.match_number,
          isCorrect,
        }
      })

      // Pool finals overlay: fetch the OFFICIAL entry's advancer order so the
      // Finals cards can show the official finalists next to the user's picks.
      // Only needed when viewing a non-official entry on a locked/complete pool
      // bracket (official results exist and differ from predictions).
      let officialPoolTiebreaks = null
      if (
        !isViewingOfficial
        && state.bracket?.format === 'pool_then_sunday'
        && ['locked', 'complete'].includes(state.bracket?.status)
        && officialEntryId
        && officialEntryId !== entryIdToFetch
      ) {
        try {
          const off = await fetchEntryById(officialEntryId)
          officialPoolTiebreaks = off?.pool_tiebreaks || null
        } catch (err) {
          console.error('Error fetching official advancer order:', err)
        }
      }

      set({ picks: loadedPicks, originalPicks: loadedPicks, officialPoolTiebreaks })
      get().recomputeDerivedState()
    } catch (err) {
      console.error('CRITICAL Error loading picks:', err)
      set({ picks: {}, originalPicks: {} })
      get().recomputeDerivedState()
    } finally {
      set({ loadingPicks: false })
    }
  },

  // ── Handler actions ──

  handleCreateEntry: async () => {
    const { bracket, bracketId, isEditingOfficial, _notify, _setSearchParams } = get()

    // Admin is in edit-official mode — user-level entries cannot be created
    // from inside the admin dashboard. Leave the admin flow and navigate as
    // a player to make a personal entry.
    if (isEditingOfficial) {
      _notify.warning(
        'You are editing official results. To make a personal bracket entry, leave the admin dashboard and open this bracket from the player view.'
      )
      return
    }

    if (bracket?.status !== 'open') {
      _notify.warning('Brackets can only be created when the bracket is open for submissions.')
      return
    }

    try {
      set({ joining: true })
      const data = await createEntry(bracketId, false, null)
      await get().loadUserEntries()
      // Preserve any other URL params (e.g. group filters) that were on the
      // URL when the admin/player kicked off entry creation.
      const current = get()._searchParams
      const merged = current ? new URLSearchParams(current) : new URLSearchParams()
      merged.set('entryId', data.entry_id)
      _setSearchParams(merged)
      _notify.success('Start making your picks.')
    } catch (err) {
      console.error('Error creating entry:', err)
      _notify.error(err?.message || 'Failed to create bracket. Please try again.')
    } finally {
      set({ joining: false })
    }
  },

  handleSave: async ({ ignoreMissingPicks = false } = {}) => {
    const state = get()
    const { isViewingOfficial, isAdmin, picks, matches, bracketId, officialEntryId, selectedEntryId, isNewEntryMode, _notify, _setSearchParams, bracketStructure } = state

    if (isViewingOfficial && !isAdmin) {
      await _notify.alert({
        title: 'No Bracket Selected',
        description: 'You are viewing official results. Select or create a bracket to save picks.',
        variant: 'info',
      })
      return
    }

    const isOfficialSave = isViewingOfficial && isAdmin

    if (!isOfficialSave && !ignoreMissingPicks) {
      const missing = []
      const isPoolFormat = state.bracket?.format === 'pool_then_sunday'

      if (isPoolFormat) {
        const poolStructure = state.poolStructure
        if (poolStructure) {
          poolStructure.pools.forEach(pool => {
            pool.matches.forEach(m => {
              const pick = picks[m.match_id]
              if (!pick?.teamId) {
                missing.push({
                  round: m.round,
                  roundLabel: `Pool ${pool.pool_id}`,
                  matchNumber: m.match_number,
                })
              }
            })
          })
          poolStructure.sunday.forEach(m => {
            if (!m.isReady) return // can't pick if standings aren't determined
            const pick = picks[m.match_id]
            if (!pick?.teamId) {
              missing.push({ round: m.round, roundLabel: 'Finals', matchNumber: m.match_number })
            }
          })
        }
      } else if (bracketStructure.length > 0) {
        bracketStructure.forEach(round => {
          // Multi-week bracket: only nag about the round(s) actually open for
          // picks. Rounds that are locked or not yet announced aren't the
          // user's to fill in.
          if (!state.canEditRound(round.roundNum)) return
          const roundLabel = getRoundLabel(round.roundNum, round.matches.length)
          round.matches.forEach(match => {
            const { team1Id, team2Id } = state.getMatchTeams(match)
            const hasTeamsAvailable = team1Id || team2Id
            const hasPick = picks[match.match_id]?.teamId
            if (hasTeamsAvailable && !hasPick) {
              missing.push({ round: round.roundNum, roundLabel, matchNumber: match.match_number })
            }
          })
        })
      }

      if (missing.length > 0) {
        missing.sort((a, b) => a.round - b.round || a.matchNumber - b.matchNumber)
        set({ missingPicks: missing, showMissingPicksModal: true })
        return
      }
    }

    try {
      // DUB-68 / DUB-69: send EVERY entry from picks state, including ones with
      // teamId = null. The server's save_entry RPC treats a null bracket_team_id
      // as an explicit deselection and DELETEs the matching pick row. Filtering
      // nulls out client-side meant deselects never reached the server, so a
      // previously-saved winner stayed in the DB and reload "undid" the deselect.
      const allPicks = Object.entries(picks).map(([matchId, pick]) => {
        let round, matchNumber
        if (matchId.startsWith('placeholder-')) {
          const parts = matchId.split('-')
          round = parseInt(parts[1].substring(1))
          matchNumber = parseInt(parts[2].substring(1))
        } else {
          const match = matches.find(m => m.match_id === matchId)
          if (!match) return null
          round = match.round
          matchNumber = match.match_number
        }
        return {
          round,
          match_number: matchNumber,
          bracket_team_id: pick.teamId ?? null,
        }
      }).filter(Boolean)

      // Multi-week bracket: drop picks for rounds this save isn't allowed to
      // touch. save_entry skips them server-side too, but sending them would
      // make every round-2 save carry the whole locked round-1 payload.
      // Gated on the round states alone -- handleSave has already decided
      // whether this save is authorized at all, and a legacy bracket (no
      // round states) sends everything exactly as before.
      const picksArray = allPicks.filter(
        p => !state.roundStates || isRoundEditable(state.roundStates, p.round, isOfficialSave)
      )
      // Rounds this save silently left behind. An admin entering results for a
      // round that isn't locked used to get "Official results saved
      // successfully!" while nothing was written and nobody was re-scored
      // (ticket 44adc0c7) -- so the dropped rounds now get named in the toast.
      const droppedRounds = [...new Set(
        allPicks.filter(p => !picksArray.includes(p)).map(p => p.round)
      )].sort((a, b) => a - b)

      let entryId

      if (isOfficialSave) {
        entryId = officialEntryId
        if (!entryId) {
          const createData = await createEntry(bracketId, true, null)
          entryId = createData.entry_id
          set({ officialEntryId: entryId })
        }
      } else {
        if (isNewEntryMode) {
          const createData = await createEntry(bracketId, false, null)
          entryId = createData.entry_id
          const current = get()._searchParams
          const merged = current ? new URLSearchParams(current) : new URLSearchParams()
          merged.set('entryId', entryId)
          _setSearchParams(merged)
          await get().loadUserEntries()
        } else {
          entryId = selectedEntryId
          if (!entryId) {
            await _notify.alert({
              title: 'No Bracket Selected',
              description: 'Please create or select a bracket first.',
              variant: 'warning',
            })
            return
          }
        }
      }

      const saveResult = await saveEntry(entryId, picksArray)

      // Persist the pool advancer/tiebreaker order for BOTH official and player
      // saves. setPoolTiebreak intentionally defers the write to here (single
      // writer), and in new-entry mode there was no entryId when the order was
      // set — without this the order (and thus the Finals matchups) is lost on
      // the reload below. The admin "tap who advances" picker writes the same
      // poolTiebreaks field, so the official save must persist it too.
      const tb = get().poolTiebreaks
      if (tb && Object.keys(tb).length > 0) {
        try {
          await savePoolTiebreaks(entryId, tb)
        } catch (e) {
          console.error('Failed to persist pool tiebreaks on save:', e)
        }
      }

      // Say what actually happened. The server reports how many picks it wrote
      // (total_picks) and how many its own round gate refused (skipped_picks);
      // combined with the rounds this client dropped, that is the difference
      // between "results are in" and "nothing was written and no score moved".
      const written = saveResult?.total_picks ?? picksArray.length
      const droppedLabel = droppedRounds
        .map(r => roundLabelFor(r, state.roundStates, bracketStructure))
        .join(', ')

      if (written === 0 && droppedRounds.length > 0) {
        _notify.warning(
          isOfficialSave
            ? `Nothing was saved — ${droppedLabel} ${droppedRounds.length > 1 ? 'are' : 'is'} not locked yet. Official results can only be entered for a locked round.`
            : `Nothing was saved — ${droppedLabel} ${droppedRounds.length > 1 ? 'are' : 'is'} not open for picks right now.`
        )
      } else if (droppedRounds.length > 0 && isOfficialSave) {
        // Player saves never take this branch: a round-locked bracket saves
        // one round at a time by design, so a player's save ALWAYS leaves the
        // locked/pending rounds behind (their picks there are already saved or
        // not yet pickable) and naming them would flag every save as partial.
        _notify.warning(
          `Official results saved, but ${droppedLabel} ${droppedRounds.length > 1 ? 'were' : 'was'} left out — that round is not locked yet.`
        )
      } else if (isOfficialSave) {
        // Scores are recalculated for every entry in the bracket, but an entry
        // with no picks in the round just scored gains nothing — which is what
        // "Enter Results didn't add any points" looked like from the outside.
        const scored = saveResult?.notified_users
        _notify.success(
          typeof scored === 'number'
            ? `Official results saved — ${scored} ${scored === 1 ? 'entry' : 'entries'} re-scored. Entries with no picks in this round score nothing for it.`
            : 'Official results saved successfully!'
        )
      } else {
        _notify.success('Picks saved successfully!')
      }

      if (isOfficialSave) {
        await get().fetchBracketData()
        await get().loadUserEntries()
        await get().loadEntryPicks(entryId)
      } else {
        await get().loadUserEntries()
        await get().loadEntryPicks(entryId)
      }
    } catch (err) {
      console.error('Error saving picks:', err)
      const prefix = isViewingOfficial && isAdmin ? 'Failed to save official results' : 'Failed to save picks'
      _notify.error(err?.message ? `${prefix}: ${err.message}` : `${prefix}. Please try again.`)
    }
  },

  handleStatusChange: async (newStatus) => {
    const { isAdmin, bracket, matches, bracketId, _notify } = get()
    if (!isAdmin) return

    if (newStatus === 'locked') {
      const confirmed = await _notify.confirm({
        title: 'Lock Bracket',
        description: 'Locking this bracket will prevent all players from submitting or editing their picks. Are you sure?',
        confirmText: 'Lock Bracket',
        cancelText: 'Cancel',
      })
      if (!confirmed) return
    }

    if (newStatus === 'open' && bracket?.status === 'draft') {
      const r1Matches = matches.filter(m => m.round === 1)
      if (r1Matches.length % 2 !== 0) {
        _notify.error('Invalid bracket')
        return
      }
    }

    try {
      await updateBracketStatus(bracketId, newStatus)
      set(state => ({ bracket: { ...state.bracket, status: newStatus } }))
      get().recomputeDerivedState()
    } catch (err) {
      console.error('Error updating bracket status:', err)
      _notify.error('Failed to update bracket status')
    }
  },

  handleToggleBracketInGroup: async (groupId, groupName, currentlyHasBracket) => {
    const { bracketId, _notify } = get()
    try {
      if (currentlyHasBracket) {
        await removeBracketFromGroup(groupId, bracketId)
        _notify.success(`Removed from ${groupName}`)
      } else {
        await addBracketToGroup(groupId, bracketId)
        _notify.success(`Added to ${groupName}`)
      }
      await get().loadOwnedGroups()
    } catch (err) {
      console.error('Error toggling bracket in group:', err)
      _notify.error(err.message || 'Failed to update group')
    }
  },

  handleSwitchEntry: (entryId) => {
    const { _searchParams, _setSearchParams } = get()
    set({ viewingLeaderboard: false })
    const merged = _searchParams ? new URLSearchParams(_searchParams) : new URLSearchParams()
    merged.set('entryId', entryId)
    _setSearchParams(merged)
  },

  handleDivisionSwitch: async (newBracketId) => {
    const { bracketId, isViewingEntry, isOwnEntry, userEntry, isViewingOfficial, isEditingOfficial, entryOwnerFirstName, _user, _navigate, _notify } = get()
    if (newBracketId === bracketId) return

    // Preserve the admin edit-official intent across division switches so an
    // admin mid-edit on bracket A doesn't silently lose edit access when
    // jumping to bracket B.
    const modeSuffix = isEditingOfficial ? '&mode=editOfficial' : ''

    // DUB-98: when the user is browsing someone else's bracket and switches
    // divisions, keep the viewing context on that user. If they have an entry
    // on the new bracket, navigate to it; if they don't, surface a notice and
    // stay put. The previous behavior fell through to the current user's own
    // entries, silently swapping the subject of the picks page mid-flow.
    const viewingOtherUser = isViewingEntry && !isOwnEntry && userEntry?.user_id
    if (viewingOtherUser) {
      try {
        const data = await fetchEntries(userEntry.user_id, newBracketId)
        const entries = data || []
        if (entries.length > 0) {
          const targetEntry = entries.find(e => e.group_id === null) || entries[0]
          _navigate(`/bracket/${newBracketId}/picks?entryId=${targetEntry.id}`)
          return
        }
        _notify?.warning?.(`${entryOwnerFirstName || 'This user'} hasn't entered this bracket`)
        return
      } catch (err) {
        console.error('Error finding entry in new bracket:', err)
        _notify?.error?.('Failed to switch brackets')
        return
      }
    }

    if (isViewingOfficial) {
      _navigate(`/bracket/${newBracketId}/picks?entryId=official${modeSuffix}`)
      return
    }

    // Viewing our own entry: carry that intent to the new bracket so the
    // user's saved picks show up instead of a blank bracket. This matters
    // for admins especially — loadUserEntries skips auto-selection when
    // isAdmin, so without an entryId in the URL admins land on a blank view.
    if (_user?.id) {
      try {
        const data = await fetchEntries(_user.id, newBracketId)
        const entries = data || []
        if (entries.length > 0) {
          const targetEntry = entries.find(e => e.group_id === null) || entries[0]
          _navigate(`/bracket/${newBracketId}/picks?entryId=${targetEntry.id}`)
          return
        }
      } catch (err) {
        console.error('Error finding own entry in new bracket:', err)
      }
    }

    _navigate(`/bracket/${newBracketId}/picks`)
  },

  handleViewOfficial: () => {
    const { _searchParams, _setSearchParams } = get()
    set({ viewingLeaderboard: false })
    const merged = _searchParams ? new URLSearchParams(_searchParams) : new URLSearchParams()
    merged.set('entryId', 'official')
    _setSearchParams(merged)
  },

  handleLeaderboardUserClick: async (entry) => {
    const { bracketId, leaderboardGroupFilter, _searchParams, _setSearchParams, _notify } = get()
    if (!entry?.user_id || !bracketId) return

    try {
      const data = await fetchEntries(entry.user_id, bracketId)
      const entries = data || []
      if (entries.length === 0) {
        _notify.warning('No entries found for this bracket.')
        return
      }

      let targetEntry = null
      if (leaderboardGroupFilter !== 'global') {
        targetEntry = entries.find(item => item.group_id === leaderboardGroupFilter)
      }
      if (!targetEntry) {
        targetEntry = entries.find(item => item.group_id === null) || entries[0]
      }
      if (!targetEntry) {
        _notify.warning('No entries found for this bracket.')
        return
      }

      const newParams = new URLSearchParams(_searchParams)
      newParams.set('entryId', targetEntry.id)
      newParams.delete('autoCreate')
      set({ viewingLeaderboard: false })
      _setSearchParams(newParams)
    } catch (err) {
      console.error('Error loading entry for leaderboard user:', err)
      _notify.error('Unable to load this entry. Please try again.')
    }
  },

  // ── Pick logic handlers ──

  handleSelectWinner: (match, teamId, scrollToMatch) => {
    if (!teamId) return
    const state = get()
    const team = state.getTeamInfo(teamId)
    if (!team) return

    const { picks, bracketStructure, isViewingOfficial } = state
    const formatTeamName = state.formatTeamName

    const oldPick = picks[match.match_id]
    const oldTeamId = oldPick?.teamId

    // Pool play matches are independent (round-robin pools + 4 derived Finals
    // matches). They do NOT feed into each other like a single-elim bracket,
    // so the downstream-clearing cascade below must NOT run — applying
    // single-elim feed math (nextMatch = round+1, ceil(match/2)) to pool
    // rounds wrongly wiped unrelated Pool B / Finals picks when a pick was
    // changed, cascading into total pick loss on edit.
    const isPoolPlay = state.bracket?.format === 'pool_then_sunday'

    // When a POOL match pick changes, any Finals (sunday) picks it fed are no
    // longer valid — the pools may now be incomplete (undetermined matchups)
    // or reordered (different pairings). Clear them so potential points and
    // the saved entry match the cleared Finals display (owner report
    // 2026-05-21: removing a pool pick cleared Finals on screen but potential
    // still counted the stale Finals points).
    const sundayMatchIds = new Set((state.matches || []).filter(m => m.match_kind === 'sunday').map(m => m.match_id))
    const changedIsSunday = sundayMatchIds.has(match.match_id)
    const clearFinalsPicks = (picksObj) => {
      if (!isPoolPlay || changedIsSunday) return
      sundayMatchIds.forEach(mid => {
        const p = picksObj[mid]
        if (p?.teamId) {
          picksObj[mid] = {
            teamId: null, teamName: null, seed: null,
            round: p.round, match_number: p.match_number,
            winner_team_id: p.winner_team_id, official_winner_name: p.official_winner_name,
            isCorrect: false,
          }
        }
      })
    }

    // Toggle: if clicking same team, deselect
    if (oldTeamId === teamId) {
      const newPicks = { ...picks }
      newPicks[match.match_id] = {
        teamId: null, teamName: null, seed: null,
        round: oldPick.round, match_number: oldPick.match_number,
        winner_team_id: oldPick.winner_team_id, official_winner_name: oldPick.official_winner_name,
        isCorrect: false,
      }

      if (isPoolPlay) {
        clearFinalsPicks(newPicks)
        set({ picks: newPicks })
        get().recomputeDerivedState()
        return
      }

      const allMatches = bracketStructure.flatMap(r => r.matches)

      if (isViewingOfficial) {
        const findNextMatch = (currentMatch) => {
          if (currentMatch.round === 0) {
            if (currentMatch.feeds_into_round && currentMatch.feeds_into_match_number) {
              return allMatches.find(m =>
                m.round === currentMatch.feeds_into_round && m.match_number === currentMatch.feeds_into_match_number
              )
            }
            return null
          }
          const nextRound = currentMatch.round + 1
          const nextMatchNumber = Math.ceil(currentMatch.match_number / 2)
          return allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber)
        }
        const clearPathDownstream = (currentMatch) => {
          const nextMatch = findNextMatch(currentMatch)
          if (!nextMatch) return
          const nextMatchPick = newPicks[nextMatch.match_id]
          if (nextMatchPick && nextMatchPick.teamId) {
            newPicks[nextMatch.match_id] = {
              teamId: null, teamName: null, seed: null,
              round: nextMatchPick.round, match_number: nextMatchPick.match_number,
              winner_team_id: nextMatchPick.winner_team_id, official_winner_name: nextMatchPick.official_winner_name,
              isCorrect: false,
            }
          }
          clearPathDownstream(nextMatch)
        }
        clearPathDownstream(match)
      } else {
        // DUB-45: clear the immediate downstream (its opponents changed) and
        // cascade only where the team that was advancing from that match still
        // appears. See app/src/lib/pickCascade.js for the detailed invariants.
        for (const clearMatchId of computeDownstreamClears(match, allMatches, newPicks)) {
          const prev = newPicks[clearMatchId]
          newPicks[clearMatchId] = {
            teamId: null, teamName: null, seed: null,
            round: prev.round, match_number: prev.match_number,
            winner_team_id: prev.winner_team_id, official_winner_name: prev.official_winner_name,
            isCorrect: false,
          }
        }
      }
      set({ picks: newPicks })
      get().recomputeDerivedState()
      return
    }

    const newPicks = {
      ...picks,
      [match.match_id]: {
        teamId: team.id,
        teamName: formatTeamName(team),
        seed: team.seed,
        round: match.round,
        match_number: match.match_number,
        ...(oldPick && {
          winner_team_id: oldPick.winner_team_id,
          official_winner_name: oldPick.official_winner_name,
          isCorrect: oldPick.isCorrect,
        }),
      },
    }

    // Clear downstream picks when a winner changes — single-elim only. Pool
    // play matches are independent, so skip the cascade entirely (it was
    // wiping unrelated pool/Finals picks).
    if (oldTeamId && oldTeamId !== teamId && !isPoolPlay) {
      const allMatches = bracketStructure.flatMap(r => r.matches)

      if (isViewingOfficial) {
        const clearPathDownstream = (currentMatch) => {
          let nextMatch = null
          if (currentMatch.round === 0) {
            if (currentMatch.feeds_into_round && currentMatch.feeds_into_match_number) {
              nextMatch = allMatches.find(m =>
                m.round === currentMatch.feeds_into_round && m.match_number === currentMatch.feeds_into_match_number
              )
            }
          } else {
            const nextRound = currentMatch.round + 1
            const nextMatchNumber = Math.ceil(currentMatch.match_number / 2)
            nextMatch = allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber)
          }
          if (!nextMatch) return
          const nextMatchPick = newPicks[nextMatch.match_id]
          if (nextMatchPick && nextMatchPick.teamId) {
            newPicks[nextMatch.match_id] = {
              teamId: null, teamName: null, seed: null,
              round: nextMatchPick.round, match_number: nextMatchPick.match_number,
              winner_team_id: nextMatchPick.winner_team_id, official_winner_name: nextMatchPick.official_winner_name,
              isCorrect: false,
            }
          }
          clearPathDownstream(nextMatch)
        }
        clearPathDownstream(match)
      } else {
        // DUB-45: clear the immediate downstream (its opponents changed) and
        // cascade only where the team that was advancing from that match still
        // appears. See app/src/lib/pickCascade.js for the detailed invariants.
        for (const clearMatchId of computeDownstreamClears(match, allMatches, newPicks)) {
          const prev = newPicks[clearMatchId]
          newPicks[clearMatchId] = {
            teamId: null, teamName: null, seed: null,
            round: prev.round, match_number: prev.match_number,
            winner_team_id: prev.winner_team_id, official_winner_name: prev.official_winner_name,
            isCorrect: false,
          }
        }
      }
    }

    // Pool play: a changed/added pool pick invalidates any Finals picks.
    clearFinalsPicks(newPicks)

    set({ picks: newPicks })
    get().recomputeDerivedState()

    // Auto-focus to next available match. Timing is owned by scrollToMatch
    // (different platforms need different delays — desktop uses double-rAF
    // to flush React's commit before measuring positions; mobile waits a
    // bit longer for the post-pick rerender + tap-feedback transitions to
    // settle so the smooth-scroll doesn't share the main thread with them).
    // DUB-106..109 (desktop) and the May-2026 mobile-jank follow-up both
    // live in useBracketScroll.js scrollToMatch.
    // Auto-advance to the next match. Pool play has its own walker (pool
    // A → pool B → Sunday). In score mode we only advance once both winner
    // AND score are set (so the user finishes this match before moving on);
    // in winner-only mode the winner alone completes the match.
    const isPoolFormat = get().bracket?.format === 'pool_then_sunday'
    if (isPoolFormat) {
      // McKay 2026-05-21: no auto-scroll while filling pool play — testers
      // found it disorienting in the two-column pool layout (Amy too). Kept
      // for single-elim, where it's well-liked.
    } else {
      const nextMatch = get().findNextAvailableMatch(match)
      if (nextMatch) scrollToMatch?.(nextMatch)
    }
  },

  // Walk pool A → pool B → Sunday in order; return the first match without a
  // winner picked. Sunday matches are skipped until their participants are
  // derived from completed pool picks + any tiebreakers.
  findNextPoolMatch: (currentMatch) => {
    const { poolStructure, picks } = get()
    if (!poolStructure) return null

    const all = []
    poolStructure.pools.forEach(p => p.matches.forEach(m => all.push(m)))
    poolStructure.sunday.forEach(m => { if (m.isReady) all.push(m) })

    const isComplete = (m) => !!picks[m.match_id]?.teamId

    const currentIdx = all.findIndex(m => m.match_id === currentMatch.match_id)
    for (let i = currentIdx + 1; i < all.length; i++) {
      if (!isComplete(all[i])) return all[i]
    }
    for (let i = 0; i < currentIdx; i++) {
      if (!isComplete(all[i])) return all[i]
    }
    return null
  },

  handleSelectChampion: (match, teamId, e) => {
    e?.stopPropagation()
    if (!teamId) return

    const state = get()
    const team = state.getTeamInfo(teamId)
    if (!team) return

    const { picks, bracketStructure } = state
    const formatTeamName = state.formatTeamName
    const newPicks = { ...picks }
    const allMatches = bracketStructure.flatMap(r => r.matches)

    let currentMatch = match
    let currentRoundNum = match.round
    let currentMatchNum = match.match_number

    while (currentMatch) {
      newPicks[currentMatch.match_id] = {
        teamId: team.id,
        teamName: formatTeamName(team),
        seed: team.seed,
        round: currentMatch.round,
        ...picks[currentMatch.match_id],
      }

      let nextMatch = null
      if (currentRoundNum === 0) {
        if (currentMatch.feeds_into_round && currentMatch.feeds_into_match_number) {
          nextMatch = allMatches.find(m =>
            m.round === currentMatch.feeds_into_round && m.match_number === currentMatch.feeds_into_match_number
          )
        }
      } else {
        const nextRound = currentRoundNum + 1
        const nextMatchNumber = Math.ceil(currentMatchNum / 2)
        nextMatch = allMatches.find(m => m.round === nextRound && m.match_number === nextMatchNumber)
      }

      currentMatch = nextMatch
      if (nextMatch) {
        currentRoundNum = nextMatch.round
        currentMatchNum = nextMatch.match_number
      }
    }

    set({ picks: newPicks })
    get().recomputeDerivedState()
  },

  // ── Lifecycle actions ──

  initialize: (bracketId, user, navigate, notify, searchParams, setSearchParams) => {
    set({
      bracketId,
      _user: user,
      _navigate: navigate,
      _notify: notify,
      _searchParams: searchParams,
      _setSearchParams: setSearchParams,
      isAdmin: user?.role === 'admin',
    })
  },

  /** Sync mutable external deps without resetting data (called on every render) */
  syncDeps: (user, navigate, notify, searchParams, setSearchParams) => {
    const state = get()
    // Only set if changed to avoid unnecessary renders
    if (state._user !== user || state._navigate !== navigate || state._notify !== notify || state._searchParams !== searchParams || state._setSearchParams !== setSearchParams) {
      // Derived state (canEditPicks, isEditingOfficial) depends on the
      // mode query param, so bounce through recomputeDerivedState whenever
      // the searchParams ref changes.
      const searchParamsChanged = state._searchParams !== searchParams
      set({
        _user: user,
        _navigate: navigate,
        _notify: notify,
        _searchParams: searchParams,
        _setSearchParams: setSearchParams,
      })
      if (searchParamsChanged) get().recomputeDerivedState()
    }
  },

  /** Sync entryIdFromUrl and selectedEntryId from URL */
  syncEntryFromUrl: (entryIdFromUrl) => {
    const prev = get().entryIdFromUrl
    if (prev !== entryIdFromUrl) {
      set({
        entryIdFromUrl,
        selectedEntryId: entryIdFromUrl || null,
      })
      get().recomputeDerivedState()
    }
  },

  reset: () => {
    set({
      tournament: null,
      bracket: null,
      tournamentBrackets: [],
      matches: [],
      teams: [],
      poolRosters: [],
      poolStructure: null,
      picks: {},
      originalPicks: {},
      loading: true,
      loadingPicks: false,
      error: null,
      userEntries: [],
      userEntriesLoaded: false,
      userEntry: null,
      officialEntryId: null,
      entryOwnerFirstName: null,
      entryOwnerProfile: null,
      viewerHiddenRounds: [],
      showJoinModal: false,
      userGroups: [],
      availableGroups: [],
      selectedGroupId: 'global',
      joining: false,
      showMissingPicksModal: false,
      missingPicks: [],
      isLockedBannerDismissed: false,
      ownedGroups: [],
      loadingOwnedGroups: false,
      viewingLeaderboard: false,
      leaderboard: [],
      leaderboardLoading: false,
      leaderboardGroupFilter: 'global',
      bracketId: null,
      mobileRoundIndex: 0,
      displayRoundIndex: 0,
      showBackToTop: false,
      inlineSaveInView: true,
      isViewingOfficial: false,
      isEditingOfficial: false,
      isAdminBracketContext: false,
      isNewEntryMode: false,
      isViewingEntry: false,
      isOwnEntry: false,
      canEditPicks: false,
      roundStates: null,
      usesRoundLocks: false,
      bracketStructure: [],
      bracketRoundCount: 0,
      hasPicksChanged: false,
      hasAnyPicks: false,
      tiebreaksDirty: false,
      poolTiebreaks: {},
      officialPoolTiebreaks: null,
      userSaveLabel: 'Edit picks',
      liveStats: { potentialPoints: 0, hasChampionPick: false, championTeamId: null, championWon: false },
      matchCellInfo: new Map(),
    })
  },
}))

export default useBracketStore
